import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { MatStepper, MatStepperModule } from '@angular/material/stepper';
import { MatChipInputEvent } from '@angular/material/chips';
import { COMMA, ENTER } from '@angular/cdk/keycodes';
import { TextFieldModule } from '@angular/cdk/text-field';
import { Subscription, combineLatest } from 'rxjs';
import { MaterialModule } from '../../material/material.module';
import { AI_PROCESS_DESCRIPTION_MAX, CURRENT_STATE_PROJECT_KEY, MODEL_TYPE } from '../../common/linqset-model';
import { createModelDetails, createOrganisation, INDUSTRY, MODEL_DESCRIPTION_MAX, MODEL_FUNCTION, MODEL_NAME_MAX, MODEL_NAME_MIN, ModelDetails, ORGANISATION_NAME_MAX, ORGANISATION_NAME_MIN, Organisation, Project } from '../../common/organisation';
import { USER_ROLE } from '../../common/user';
import { getUserMessage, LinqException } from '../../common/exceptions';
import { DEFAULT_URL, PROJECTS_URL, SKETCH_URL } from '../../app-urls';
import { environment } from '../../../environments/environment';
import { offerStartChoices } from '../../user/onboarding';
import { UserService } from '../../user/user.service';
import { FUNCTION, FunctionService } from '../../model-services/function.service';
import { IMAGE_LINQ } from '../../model-services/model-item-enums';
import { OrganisationService } from '../../organisation/organisation.service';
import { OnboardingService } from '../../user/onboarding.service';
import { AIServices } from '../../ai/ai-services';
import { AI_FILE_ACCEPT, checkAIFile } from '../../ai/ai-files';
import { addAnswer, PROCESS_QUESTIONS, ProcessAnswers, suggestModelName, toAIData } from './get-started';

// AI can take minutes; the browser gives up first (23), or the gateway does (504)
const isTimeout = (err:any) => err?.httpStatus === 23 || err?.httpStatus === 504
const problem = (err:any, fallback:string) => getUserMessage(err as LinqException) || fallback

// the slow steps say more if they are still going after this long
const SLOW_MS = 60000

// getting started: sets up an organisation (or uses the empty one the user has) and creates its first model with AI.
// The organisation and model are created once the process is chosen, so AI can describe it; from then on the first
// two steps are fixed, and the user can describe the process again as often as they like before building it.
// Creating any other model with AI comes here too (?create=true), in the project given (&project=key). An empty model
// already made can be built here too (&model=key): its details are kept, and AI builds it rather than a new model
@Component({
    selector: 'app-get-started',
    templateUrl: './get-started.html',
    styleUrls: ['./get-started.scss'],
    standalone: true,
    imports: [MaterialModule, ReactiveFormsModule, MatStepperModule, TextFieldModule]
})
export class GetStartedComponent implements OnInit, OnDestroy {
    IMAGE_LINQ = IMAGE_LINQ
    industries = Object.values(INDUSTRY)
    functions = Object.values(MODEL_FUNCTION)
    questions = PROCESS_QUESTIONS
    separatorKeys = [ENTER, COMMA]
    AI_FILE_ACCEPT = AI_FILE_ACCEPT
    ORGANISATION_NAME_MIN = ORGANISATION_NAME_MIN
    ORGANISATION_NAME_MAX = ORGANISATION_NAME_MAX
    MODEL_NAME_MIN = MODEL_NAME_MIN
    MODEL_NAME_MAX = MODEL_NAME_MAX
    MODEL_DESCRIPTION_MAX = MODEL_DESCRIPTION_MAX
    AI_PROCESS_DESCRIPTION_MAX = AI_PROCESS_DESCRIPTION_MAX

    loading = true
    choosing = false                          // someone with no organisation choosing how to start: the demo or their own
    private startCreating = false             // they already chose to create their organisation
    organisation:Organisation|undefined       // the user's existing organisation, if they have one
    adding = false                            // another model for the organisation, rather than getting started
    existingModel:ModelDetails|undefined      // an empty model already made, for AI to build
    project:Project|undefined                 // the project the model is created in, if not current state
    private suggestedName = ''                // the name last suggested, replaced while the user hasn't changed it

    organisationForm = this.formBuilder.group({
        name: ['', [Validators.required, Validators.minLength(ORGANISATION_NAME_MIN), Validators.maxLength(ORGANISATION_NAME_MAX)]],
        industry: [null as INDUSTRY|null, Validators.required],
    })
    processForm = this.formBuilder.group({
        function: [null as MODEL_FUNCTION|null, Validators.required],
        name: ['', [Validators.required, Validators.minLength(MODEL_NAME_MIN), Validators.maxLength(MODEL_NAME_MAX)]],
        description: ['', [Validators.required, Validators.maxLength(MODEL_DESCRIPTION_MAX)]],
    })
    answers:Record<'starts'|'roles'|'systems'|'outcomes', string[]> = {starts:[], roles:[], systems:[], outcomes:[]}
    more = new FormControl('', {nonNullable:true})
    file:File|undefined
    fileType:string|undefined
    fileError = ''

    created:{organisationKey:string, modelKey:string}|undefined
    creating = false
    createError = ''

    description = new FormControl('', {nonNullable:true, validators:[Validators.required, Validators.maxLength(AI_PROCESS_DESCRIPTION_MAX)]})
    describing = false
    describeError = ''

    building = false
    buildError = ''
    buildTimedOut = false

    slow = false
    private slowTimer:any
    private routeSub:Subscription
    private functionSub:Subscription

    stepper:MatStepper
    private startAt = 0                     // the step to open at, once the steps are drawn

    // the steps are drawn once loading finishes; they open at the first step there is something to do in
    @ViewChild(MatStepper) set stepperRef(stepper:MatStepper|undefined) {
        this.stepper = stepper as MatStepper
        if (stepper && this.startAt) {
            const index = this.startAt
            this.startAt = 0
            setTimeout(() => stepper.selectedIndex = index)
        }
    }

    constructor(private formBuilder:FormBuilder, private route:ActivatedRoute, private router:Router, private organisationService:OrganisationService,
        private onboardingService:OnboardingService, private aiServices:AIServices, private userService:UserService, private functionService:FunctionService) {}

    ngOnInit(): void {
        this.functionSub = this.processForm.controls.function.valueChanges.subscribe(f => this.suggestName(f))
        // the query says what to make (another model, in a project, or an existing empty one), so a change to it reloads too
        this.routeSub = combineLatest([this.route.params, this.route.queryParamMap]).subscribe(([p, query]) => {
            this.adding = query.get('create') === 'true'
            this.startCreating = query.get('start') === 'create'
            this.load(p['organisation_key'], query.get('project') || undefined, query.get('model') || undefined)
        })
    }

    ngOnDestroy(): void {
        this.routeSub?.unsubscribe()
        this.functionSub?.unsubscribe()
        clearTimeout(this.slowTimer)
    }

    // the workspace the model is in
    private get workspaceKey() {
        return this.project?._key || CURRENT_STATE_PROJECT_KEY
    }

    private async load(organisationKey?:string, projectKey?:string, modelKey?:string) {
        this.loading = true
        // the page is kept when only the address's query changes, so nothing carries over from the last model
        this.existingModel = this.created = undefined
        this.startAt = 0
        this.processForm.enable()
        this.processForm.reset()
        try {
            if (!organisationKey) return
            const organisation:Organisation = await this.organisationService.getOrganisation(organisationKey)
            // only someone who can add models gets started here
            if (!organisation.role || organisation.role > USER_ROLE.WRITE) {
                this.router.navigateByUrl(DEFAULT_URL(organisationKey))
                return
            }
            this.organisation = organisation
            this.project = projectKey ? organisation.projects?.find(p => p._key === projectKey) : undefined
            const {name, industry} = this.organisationForm.controls
            name.setValue(organisation.name)
            name.disable()
            // an empty model already made: AI builds it, with its own details, so they aren't asked for again
            const model = this.adding && modelKey ? organisation.models?.find(m => m._key === modelKey) : undefined
            if (model) {
                this.existingModel = model
                this.processForm.setValue({function: model.function || null, name: model.name || '', description: model.description?.trim() || model.name || ''})
                this.processForm.disable()
                this.created = {organisationKey:organisation._key, modelKey:model._key}
            }
            if (organisation.industry) {
                industry.setValue(organisation.industry)
                industry.disable()
                // nothing to ask about the organisation, so another model starts with its process, and an existing
                // model with telling AI about it
                if (this.adding) this.startAt = model ? 2 : 1
            }
        }catch(err) {
            // already shown; the user can still set up a new organisation
            this.organisation = undefined
        }finally {
            this.choosing = !this.adding && !this.startCreating && offerStartChoices(this.userService.getUser(), !!this.organisation, environment.demoOrganisationKey)
            this.loading = false
        }
    }

    // the ways to start, for someone with no organisation of their own. Either way into the demo, they aren't sent back
    // here until they next come in
    walkThroughDemo() {
        this.onboardingService.walkThroughDemo()
    }

    openDemoOrganisation() {
        this.onboardingService.exploreDemo()
    }

    learnTheBasics() {
        this.onboardingService.learnTheBasics()
    }

    // the signed-in user, for the account menu
    get user() {
        return this.userService.getUser()
    }

    // as signing out from the app's menu
    signOut() {
        this.functionService.executeFunction(FUNCTION.LOGOUT)
    }

    createOwnOrganisation() {
        this.choosing = false
    }

    // a suggested name follows the function chosen, until the user writes their own
    private suggestName(modelFunction:MODEL_FUNCTION|null) {
        const name = this.processForm.controls.name
        if (!modelFunction || (name.value && name.value !== this.suggestedName)) return
        this.suggestedName = suggestModelName(modelFunction)
        name.setValue(this.suggestedName)
    }

    addAnswer(key:keyof GetStartedComponent['answers'], event:MatChipInputEvent) {
        this.answers[key] = addAnswer(this.answers[key], event.value)
        event.chipInput.clear()
    }

    removeAnswer(key:keyof GetStartedComponent['answers'], item:string) {
        this.answers[key] = this.answers[key].filter(a => a !== item)
    }

    onFileSelected(event:Event) {
        const input = event.target as HTMLInputElement
        const file = input.files?.[0]
        input.value = ''            // so choosing the same file again still counts
        if (!file) return

        const checked = checkAIFile(file)
        if ('error' in checked) {
            this.fileError = checked.error
            return
        }
        this.fileError = ''
        this.file = file
        this.fileType = checked.fileType
    }

    removeFile() {
        this.file = this.fileType = undefined
    }

    private processAnswers():ProcessAnswers {
        const organisation = this.organisationForm.getRawValue()
        const process = this.processForm.getRawValue()
        return {
            industry: organisation.industry || '',
            function: process.function || '',
            description: process.description || '',
            ...this.answers,
            more: this.more.value,
        }
    }

    // the end of the second step: the organisation and model are created, so AI can work on them
    async createModel() {
        this.processForm.markAllAsTouched()
        if (this.processForm.invalid || this.creating) return
        // an existing model only needs the organisation's industry, if it was just given
        if (this.created && this.existingModel && this.organisation && !this.organisation.industry) {
            const industry = this.organisationForm.getRawValue().industry
            if (industry) {
                this.creating = true
                try {
                    this.organisation.industry = industry
                    await this.organisationService.saveGettingStartedOrganisation(this.organisation, false)
                }catch(err) {
                    this.createError = problem(err, "Your organisation's industry couldn't be saved. Please try again.")
                    return
                }finally {
                    this.creating = false
                }
            }
        }
        if (this.created) return this.stepper.next()

        this.creating = true
        this.createError = ''
        try {
            const organisationValues = this.organisationForm.getRawValue()
            if (!this.organisation) {
                const organisation = createOrganisation()
                organisation.name = organisationValues.name.trim()
                organisation.industry = organisationValues.industry
                await this.organisationService.saveGettingStartedOrganisation(organisation, true)
                // it exists now, so trying again after the model fails only adds the model
                this.organisation = organisation
            }else if (this.organisation.industry !== organisationValues.industry) {
                this.organisation.industry = organisationValues.industry
                await this.organisationService.saveGettingStartedOrganisation(this.organisation, false)
            }

            const values = this.processForm.getRawValue()
            const model = createModelDetails(undefined, undefined, this.workspaceKey)
            model.name = values.name.trim()
            model.function = values.function
            model.description = values.description.trim()

            await this.organisationService.addModelDetails(this.organisation._key, model)
            this.created = {organisationKey:this.organisation._key, modelKey:model._key}
            // wait for the steps to see they can no longer be changed
            setTimeout(() => this.stepper.next())
        }catch(err) {
            this.createError = problem(err, "Your model couldn't be created. Please try again.")
        }finally {
            this.creating = false
        }
    }

    async describe() {
        if (!this.created || this.describing) return
        this.stepper.next()
        this.describing = true
        this.describeError = ''
        this.startSlowTimer()
        try {
            const {organisationKey, modelKey} = this.created
            const description = await this.aiServices.describeProcess(organisationKey, modelKey, this.workspaceKey, toAIData(this.processAnswers(), this.fileType), this.file, false)
            if (!description) throw new Error("No description")
            this.description.setValue(description)
        }catch(err) {
            this.describeError = isTimeout(err)
                ? "AI is taking longer than usual to describe your process. Try again, or build your model without checking the description."
                : problem(err, "AI couldn't describe your process. Try again, or build your model without checking the description.")
        }finally {
            this.describing = false
            this.stopSlowTimer()
        }
    }

    // builds the model from the description the user checked or, if AI couldn't give one, from their answers
    async build(useDescription:boolean = true) {
        if (!this.created || this.building) return
        if (useDescription && this.description.invalid) return

        this.building = true
        this.buildError = ''
        this.buildTimedOut = false
        this.startSlowTimer()
        try {
            const {organisationKey, modelKey} = this.created
            const answers = this.processAnswers()
            const aiData = useDescription ? toAIData(answers, undefined, this.description.value) : toAIData(answers, this.fileType)
            await this.aiServices.generateModel(organisationKey, modelKey, this.workspaceKey, aiData, true, useDescription ? undefined : this.file, false)
            this.openModel()
        }catch(err) {
            this.buildTimedOut = isTimeout(err)
            this.buildError = this.buildTimedOut
                ? "AI is still building your model. It saves automatically when it's done, so open your model in a minute or two."
                : problem(err, "AI couldn't build your model. Please try again.")
        }finally {
            this.building = false
            this.stopSlowTimer()
        }
    }

    // the model screen welcomes a model that getting started built
    openModel() {
        if (!this.created) return
        this.router.navigateByUrl(SKETCH_URL(this.created.organisationKey, this.created.modelKey, MODEL_TYPE.PROCESS, this.workspaceKey) + '?reloadorg=true&welcome=true')
    }

    // the empty model, with the AI panel to use later
    startBlank() {
        if (!this.created) return
        this.router.navigateByUrl(SKETCH_URL(this.created.organisationKey, this.created.modelKey, MODEL_TYPE.PROCESS, this.workspaceKey) + '?reloadorg=true')
    }

    // getting started can be left for later; another model is just not created (or kept blank, if it already was)
    notNow() {
        // an existing model is kept as it is: back to it
        if (this.existingModel && this.created) {
            this.router.navigateByUrl(SKETCH_URL(this.created.organisationKey, this.created.modelKey, MODEL_TYPE.PROCESS, this.workspaceKey))
            return
        }
        if (this.adding && this.organisation) {
            this.router.navigateByUrl(this.project ? PROJECTS_URL(this.organisation._key, this.project._key) : DEFAULT_URL(this.organisation._key))
            return
        }
        this.onboardingService.dismissGettingStarted(this.created?.organisationKey || this.organisation?._key)
    }

    private startSlowTimer() {
        this.slow = false
        clearTimeout(this.slowTimer)
        this.slowTimer = setTimeout(() => this.slow = true, SLOW_MS)
    }

    private stopSlowTimer() {
        clearTimeout(this.slowTimer)
        this.slow = false
    }
}
